/**
 * Firebase initialization. All Firebase access goes through the `auth` and
 * `db` exports so emulator wiring happens in exactly one place.
 *
 * Set REACT_APP_USE_FIREBASE_EMULATOR=true to run against the local
 * emulators (see `npm run dev:firebase`); no real Firebase project or web
 * config is needed in that mode.
 */
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';

// Unit tests (jest) also use the demo config — they never talk to a real
// project, and services are mocked or run against the emulator.
// A production build can never target the emulators, whatever .env says.
const useEmulator =
  process.env.NODE_ENV !== 'production' &&
  (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true' ||
    process.env.NODE_ENV === 'test');

const firebaseConfig = useEmulator
  ? {
      apiKey: 'demo-api-key',
      authDomain: 'localhost',
      projectId: 'demo-prompt-library',
    }
  : {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    };

// Exported for secondary app instances (e.g. admin user creation, which
// must not replace the admin's own session).
export { firebaseConfig, useEmulator };

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);

if (useEmulator) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
}

/**
 * The email domain whose verified users are treated as app admins.
 * Must match the constant in firestore.rules — the rules are the actual
 * enforcement; this is only used to shape the UI.
 */
export const ADMIN_EMAIL_DOMAIN =
  process.env.REACT_APP_ADMIN_EMAIL_DOMAIN || 'alto-education.com';

/**
 * Email domains allowed to sign up. Mirrors the allowlist in firestore.rules
 * (the enforcement); this copy only drives sign-up form validation.
 */
export const ALLOWED_SIGNUP_DOMAINS = (
  process.env.REACT_APP_ALLOWED_SIGNUP_DOMAINS || 'defence.gov.au,alto-education.com,qinetiq.com.au'
)
  .split(',')
  .map((d) => d.trim().toLowerCase())
  .filter(Boolean);

/** Extract the org ID (email domain) from an email address. */
export const orgIdFromEmail = (email) =>
  (email || '').split('@')[1]?.toLowerCase() || null;

/** Whether a Firebase Auth user is an app admin (UI convenience only). */
export const isAdminUser = (user) =>
  Boolean(user && user.emailVerified && orgIdFromEmail(user.email) === ADMIN_EMAIL_DOMAIN);
